// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// packages/react/src/lib/motion.ts

/**
 * Geenius Analytics React - Motion Helpers
 *
 * @module ReactMotionHelpers
 * @description Centralizes the small transition contract used by React
 * analytics components so animated surfaces share one duration and delay model.
 */

import {
  motion,
  type HTMLMotionProps,
  type Transition,
  useReducedMotion,
} from "motion/react";
import type { ReactElement, ReactNode } from "react";
import { createElement } from "react";
import { cn } from "./cn";

const DEFAULT_FADE_IN_DURATION_SECONDS = 0.28;
const FADE_IN_DATA_ATTRIBUTES = { "data-motion": "fade-in" } as const;

export const ANALYTICS_MOTION_DELAYS = {
  none: 0,
  stats: 60,
  overview: 120,
  events: 180,
} as const;

export interface FadeInTransition {
  duration: number;
  ease: "easeOut";
  delay: number;
}

export interface FadeInProps
  extends Omit<HTMLMotionProps<"div">, "children" | "transition"> {
  children: ReactNode;
  transition?: FadeInTransition;
}

export function getFadeInTransition(delayMs = 0): FadeInTransition {
  return {
    duration: DEFAULT_FADE_IN_DURATION_SECONDS,
    ease: "easeOut",
    delay: delayMs / 1000,
  };
}

export function FadeIn({
  children,
  className,
  style,
  transition = getFadeInTransition(),
  ...rest
}: FadeInProps): ReactElement {
  const shouldReduceMotion = useReducedMotion();
  const motionTransition: Transition = shouldReduceMotion
    ? { duration: 0 }
    : transition;

  return createElement(
    motion.div,
    {
      animate: { opacity: 1, y: 0 },
      className: cn("motion-reduce:transition-none", className),
      ...FADE_IN_DATA_ATTRIBUTES,
      initial: shouldReduceMotion ? false : { opacity: 0, y: 8 },
      style,
      transition: motionTransition,
      ...rest,
    },
    children,
  );
}
